import * as React from 'react';
import styles from './index.less';
import { AutoComplete, Dropdown, Input, Popover, Space } from 'antd';
import * as AHooks from 'ahooks';
import { FI18n, FServiceAPI, FUtil } from '@freelog/tools-lib';
import FComponentsLib from '@freelog/components-lib';
import FTooltip from '@/components/FTooltip';
import FIcons from '@/components/FIcons';
import FSimpleBar from '../FSimpleBar';
import FInput_PinyinSafeTextCounter from '../FInput_PinyinSafeTextCounter';

interface Option {
  value: string;
  label: string;
  note: string;
  values: Array<string | number>;
  labels: string[];
  children: Option[];
}

interface ServerData {
  code: string;
  name: string;
  note: string;
  children: ServerData[];
}

interface FResourceTypeInput3Props {
  disabled?: boolean;
  value: {
    value: string;
    labels: string[];
    customInput?: string;
  } | null;

  onChange?(value: FResourceTypeInput3Props['value']): void;

  subjectType?: 'resource' | 'collection' | 'batchResources' | 'exhibitCollection';

  onlySelect?: boolean;

  width?: number; // New property to control the width

  showAddNewType?: boolean;

  placeholder?: string;
}

interface FResourceTypeInput3States {
  $options: Option[];
  $recommend: {
    value: string;
    labels: string[];
  }[];

  _selectedCache: {
    value: string;
    label: string;
    values: Array<string | number>;
    labels: string[];
  } | null;
  _autoCompleteOptions: {
    value: string;
    label: string;
    values: string[];
    labels: string[];
    count: number;
  }[];
  _autoCompleteOptionsOther: {
    value: string;
    label: string;
    values: string[];
    labels: string[];
    count: number;
  }[];
  _autoCompleteInput: string;
  _autoCompleteInputIsNew: boolean;
}

const initStates: FResourceTypeInput3States = {
  $options: [],
  $recommend: [],
  _selectedCache: null,
  _autoCompleteOptions: [],
  _autoCompleteOptionsOther: [],
  _autoCompleteInput: '',
  _autoCompleteInputIsNew: false,
};

function FResourceTypeInput3({
  disabled = false,
  value,
  onChange,
  subjectType = 'resource',
  onlySelect = false,
  width = 500,
  showAddNewType = true,
  placeholder = FI18n.i18nNext.t('createresource_selectresourcetype_input_hint'),
}: FResourceTypeInput3Props) {
  const ref = React.useRef<any>();
  const ref_input = React.useRef<any>();

  const [$state, $setState] = AHooks.useSetState<FResourceTypeInput3States>(initStates);

  const [$searchInput, set$searchInput, get$searchInput] = FUtil.Hook.useGetState<string>('');
  const [$searchInputState, set$searchInputState, get$searchInputState] = FUtil.Hook.useGetState<
    'focus' | 'blur'
  >('blur');
  const [$searchResult, set$searchResult, get$searchResult] = FUtil.Hook.useGetState<
    {
      value: string;
      label: string;
      values: string[];
      labels: string[];
      count: number;
    }[]
  >([]);
  const [$dropdownOverlayShow, set$dropdownOverlayShow, get$dropdownOverlayShow] =
    FUtil.Hook.useGetState<'search' | 'list' | ''>('');

  const $dropdownOverlayShow_Debounce = AHooks.useDebounce($dropdownOverlayShow, { wait: 100 });

  AHooks.useEventListener(
    'click',
    () => {
      set$dropdownOverlayShow('');
    },
    { target: window },
  );

  AHooks.useMount(async () => {
    const {
      data: data_resourceTypes,
    }: {
      data: ServerData[];
    } = await FServiceAPI.Resource.resourceTypes({
      category: 1,
      status: 1,
      subjectType:
        subjectType === 'resource' || subjectType === 'batchResources'
          ? 1
          : subjectType === 'exhibitCollection'
          ? 5
          : subjectType === 'collection'
          ? 4
          : undefined,
      supportCreateBatch: subjectType === 'batchResources' ? 2 : undefined,
    });
    const options: Option[] = handledData(data_resourceTypes, null);
    $setState({
      $options: options,
    });
  });

  AHooks.useMount(async () => {
    if (subjectType === 'batchResources') {
      return;
    }
    const {
      data: data_recently,
    }: {
      data: {
        code: string;
        name: string;
        names: string;
        resourceCount: number;
      }[];
    } = await FServiceAPI.Resource.listSimple4Recently({
      subjectType: subjectType === 'resource' ? 1 : 4,
    });
    if (!data_recently) {
      return;
    }

    $setState({
      $recommend: data_recently
        .filter((r, i) => {
          return i < 6;
        })
        .map<FResourceTypeInput3States['$recommend'][number]>((r) => {
          return {
            value: r.code,
            labels: r.names.split('/'),
          };
        }),
    });
  });

  AHooks.useDebounceEffect(
    () => {
      (async () => {
        if ($state._autoCompleteInput === '' || $state._autoCompleteInput.endsWith('/')) {
          $setState({
            _autoCompleteOptions: [],
            _autoCompleteOptionsOther: [],
          });
          return;
        }
        const search: string[] = $state._autoCompleteInput.split('/');

        const {
          data: data_list,
        }: {
          data: {
            code: string;
            name: string;
            names: string;
            resourceCount: number;
          }[];
        } = await FServiceAPI.Resource.ListSimpleByParentCode({
          parentCode: $state._selectedCache?.value || '',
          name: search[search.length - 1],
          // name: search.at(-1),
          isTerminate: true,
        });

        $setState({
          _autoCompleteOptions: data_list.map((l) => {
            return {
              value: l.code,
              label: l.name,
              values: [l.code],
              labels: l.names.split('/'),
              count: l.resourceCount,
            };
          }),
        });

        const {
          data: data_list1,
        }: {
          data: {
            code: string;
            name: string;
            names: string;
            resourceCount: number;
          }[];
        } = await FServiceAPI.Resource.ListSimpleByParentCode({
          parentCode: $state._selectedCache?.value || '',
          category: 1,
          name: search[search.length - 1],
          excludeParentCode: true,
          isTerminate: true,
        });

        $setState({
          _autoCompleteOptionsOther: data_list1.map((l) => {
            return {
              value: l.code,
              label: l.name,
              values: [l.code],
              labels: l.names.split('/'),
              count: l.resourceCount,
            };
          }),
        });
      })();
    },
    [$state._autoCompleteInput],
    {
      wait: 300,
    },
  );

  function init() {
    $setState({
      _selectedCache: initStates['_selectedCache'],
      _autoCompleteOptions: initStates['_autoCompleteOptions'],
      _autoCompleteInput: initStates['_autoCompleteInput'],
      _autoCompleteInputIsNew: initStates['_autoCompleteInputIsNew'],
    });
  }

  function onDropdownChange(v: { value: string; labels: string[] }) {
    onChange &&
      onChange({
        value: v.value,
        labels: v.labels,
      });
    set$searchInput('');
    set$dropdownOverlayShow('');
  }

  async function onDropdownClickCustom({
    value,
    values,
    label,
    labels,
  }: {
    value: string;
    label: string;
    values: Array<string | number>;
    labels: string[];
  }) {
    const startWidth: string = [...labels, ''].join('/');

    $setState({
      _autoCompleteInput: startWidth,
      _selectedCache: {
        value,
        values,
        label,
        labels,
      },
    });
    set$dropdownOverlayShow('');
  }

  const { run: run_Search } = AHooks.useDebounceFn(
    async () => {
      if (get$searchInput() === '') {
        set$dropdownOverlayShow('list');
        return;
      }

      const {
        data: data_list1,
      }: {
        data: {
          code: string;
          name: string;
          names: string;
          resourceCount: number;
        }[];
      } = await FServiceAPI.Resource.ListSimpleByParentCode({
        nameChain: get$searchInput(),
        subjectType:
          subjectType === 'resource' || subjectType === 'batchResources'
            ? 1
            : subjectType === 'collection'
            ? 4
            : subjectType === 'exhibitCollection'
            ? 5
            : undefined,
        supportCreateBatch: subjectType === 'batchResources' ? 2 : undefined,
        isTerminate: true,
        status: 1,
      });

      set$searchResult(
        data_list1.map((l) => {
          return {
            value: l.code,
            label: l.name,
            values: [l.code],
            labels: l.names.split('/'),
            count: l.resourceCount,
          };
        }),
      );
      set$dropdownOverlayShow('search');
    },
    {
      wait: 300,
    },
  );

  if ($state._selectedCache) {
    return (
      <div style={{ display: 'block' }}>
        <AutoComplete
          dropdownStyle={{ padding: 0 }}
          disabled={disabled}
          autoFocus={true}
          defaultOpen={true}
          options={[
            ...($state._autoCompleteInputIsNew && !onlySelect
              ? [
                  {
                    value: '#new',
                    label: (
                      <div className={styles.autoCompleteOption}>
                        <FTooltip
                          overlayInnerStyle={{
                            width: 'fit-content',
                          }}
                          title={$state._autoCompleteInput}
                          mouseEnterDelay={0.5}
                        >
                          <span>{$state._autoCompleteInput}</span>
                        </FTooltip>
                        {/*<FComponentsLib.FTextBtn>添加新类型</FComponentsLib.FTextBtn>*/}
                        <FComponentsLib.FTextBtn>
                          {FI18n.i18nNext.t('createresource_selectresourcetype_btn_addthis')}
                        </FComponentsLib.FTextBtn>
                      </div>
                    ),
                    data: {
                      value: '#new',
                      label: '',
                      values: [],
                      labels: $state._autoCompleteInput.split('/'),
                    },
                  },
                ]
              : []),
            ...$state._autoCompleteOptions.map((aco) => {
              return {
                value: aco.value,
                label: (
                  <div className={styles.autoCompleteOption}>
                    <FTooltip
                      overlayInnerStyle={{
                        width: 'fit-content',
                      }}
                      title={aco.labels.join('/')}
                      mouseEnterDelay={0.5}
                    >
                      <span>{aco.labels.join('/')}</span>
                    </FTooltip>
                    <FComponentsLib.FContentText
                      // text={`${aco.count}个资源`}
                      text={FI18n.i18nNext.t(
                        'createresource_selectresourcetype_input_resourceqty',
                        {
                          ResourceQty: aco.count,
                        },
                      )}
                      type={'additional2'}
                    />
                  </div>
                ),
                data: aco,
              };
            }),
            ...$state._autoCompleteOptionsOther.map((aco) => {
              return {
                value: aco.value,
                label: (
                  <div className={styles.autoCompleteOption}>
                    <FTooltip
                      overlayInnerStyle={{
                        width: 'fit-content',
                      }}
                      title={aco.labels.join('/')}
                      mouseEnterDelay={0.5}
                    >
                      <span>{aco.labels.join('/')}</span>
                    </FTooltip>
                    <FComponentsLib.FContentText
                      // text={`${aco.count}个资源`}
                      text={FI18n.i18nNext.t(
                        'createresource_selectresourcetype_input_resourceqty',
                        {
                          ResourceQty: aco.count,
                        },
                      )}
                      type={'additional2'}
                    />
                  </div>
                ),
                data: aco,
              };
            }),
          ]}
          style={{ width }}
          value={$state._autoCompleteInput}
          className={styles.AutoComplete}
          onChange={(value: string) => {
            if (!value) {
              return;
            }
            const startStr: string = [...($state._selectedCache?.labels || []), ''].join('/');
            if (!value?.startsWith(startStr)) {
              return;
            }

            const custom: string = value.replace(startStr, '');

            if (value.length <= startStr.length + 40) {
              $setState({
                _autoCompleteInputIsNew:
                  value !== startStr &&
                  FUtil.Regexp.RESOURCE_TYPE.test(custom) &&
                  $state._autoCompleteOptions.every((aco) => {
                    return aco.labels.join('/') !== value;
                  }),
                _autoCompleteInput: value,
              });
            }
          }}
          onSelect={(value: any, op: any) => {
            const data: FResourceTypeInput3States['_autoCompleteOptions'][number] = op.data;
            if (value !== '#new') {
              onDropdownChange({
                value: data.value,
                labels: data.labels,
              });
            } else {
              const customInputLabels: string[] = $state._autoCompleteInput.split('/');
              onChange &&
                onChange({
                  value: $state._selectedCache?.value || '',
                  labels: $state._selectedCache?.labels || [],
                  customInput: customInputLabels[customInputLabels.length - 1],
                });
              set$searchInput($state._autoCompleteInput);
            }
            init();
          }}
          onBlur={() => {
            setTimeout(() => {
              $setState({
                _selectedCache: null,
              });
            }, 100);
          }}
        >
          {/* <FComponentsLib.FInput.FSingleLine */}
          <FInput_PinyinSafeTextCounter lengthLimit={-1} value={''} style={{ width }} />
        </AutoComplete>
        {!onlySelect && (
          <>
            <div style={{ height: 30 }} />
            <Space style={{ display: 'flex' }} size={5} direction={'vertical'}>
              <FComponentsLib.FContentText
                text={'目前仅支持中文、英文、数字，及符号“-”、“&”、“.”、“,”'}
                type={'additional2'}
              />
              <FComponentsLib.FContentText text={'英文区分大小写'} type={'additional2'} />
              <FComponentsLib.FContentText text={'长度必须在1-40个字符之间'} type={'additional2'} />
            </Space>
          </>
        )}
      </div>
    );
  }

  return (
    <Dropdown
      open={$dropdownOverlayShow_Debounce !== '' && !disabled}
      disabled={disabled}
      getPopupContainer={() => {
        return ref.current;
      }}
      destroyPopupOnHide={true}
      overlayClassName={styles.overlayClassName}
      overlayStyle={{ width, minWidth: 'auto' }}
      placement={'bottomLeft'}
      autoAdjustOverflow={false}
      dropdownRender={() => {
        return (
          <>
            {$searchInput === '' && $dropdownOverlayShow_Debounce === 'list' && (
              <FSimpleBar maxHeight={540}>
                <div
                  className={styles.overlay}
                  style={{ width }}
                  onClick={(e) => {
                    e.stopPropagation();
                  }}
                >
                  {$state.$recommend.length > 0 && (
                    <>
                      {subjectType === 'resource' && (
                        <div className={styles.recommend}>
                          <FComponentsLib.FContentText
                            text={FI18n.i18nNext.t('createresource_selectresourcetype_input_hint2')}
                            type={'additional2'}
                          />
                          <div
                            style={{
                              height: 20,
                            }}
                          />
                          <div className={styles.recommendResourceTypes}>
                            {$state.$recommend.map((r) => {
                              return (
                                <label
                                  key={r.value}
                                  onClick={() => {
                                    onDropdownChange({
                                      value: r.value,
                                      labels: r.labels,
                                    });
                                  }}
                                >
                                  {r.labels.join('/')}
                                </label>
                              );
                            })}
                          </div>
                        </div>
                      )}

                      {subjectType === 'resource' && (
                        <div
                          style={{
                            padding: '0 20px',
                          }}
                        >
                          <FComponentsLib.FContentText
                            text={FI18n.i18nNext.t('createresource_selectresourcetype_input_hint3')}
                            type={'additional2'}
                          />
                        </div>
                      )}

                      <div style={{ height: 10 }} />
                    </>
                  )}

                  <div className={styles.FCascader}>
                    {$state.$options.map((o0) => {
                      return (
                        <Popover
                          key={o0.value}
                          getPopupContainer={() => {
                            return ref.current;
                          }}
                          zIndex={10000}
                          open={o0.children.length === 0 ? false : undefined}
                          overlayClassName={styles.PopoverOverlayClassName}
                          placement={'rightTop'}
                          title={null}
                          content={
                            <div className={styles.itemChildren}>
                              {o0.children.map((o1, o1Index) => {
                                return (
                                  <React.Fragment key={o1.value}>
                                    <Popover
                                      zIndex={10001}
                                      getPopupContainer={() => {
                                        return ref.current;
                                      }}
                                      open={o1.children.length === 0 ? false : undefined}
                                      overlayClassName={styles.PopoverOverlayClassName}
                                      title={null}
                                      placement={'rightTop'}
                                      content={
                                        <div className={styles.itemChildren}>
                                          {o1.children.map((o2, o2Index) => {
                                            return (
                                              <React.Fragment key={o2.value}>
                                                <Popover
                                                  zIndex={10001}
                                                  getPopupContainer={() => {
                                                    return ref.current;
                                                  }}
                                                  open={
                                                    o2.children.length === 0 ? false : undefined
                                                  }
                                                  overlayClassName={styles.PopoverOverlayClassName}
                                                  title={null}
                                                  placement={'rightTop'}
                                                  content={
                                                    <div className={styles.itemChildren}>
                                                      {o2.children.map((o3, o3Index) => {
                                                        return (
                                                          <React.Fragment key={o3.value}>
                                                            <div
                                                              className={styles.item}
                                                              onClick={() => {
                                                                if (o3.children.length === 0) {
                                                                  onDropdownChange({
                                                                    value: o3.value,
                                                                    labels: o3.labels,
                                                                  });
                                                                } else {
                                                                  ref_input.current?.focus();
                                                                }
                                                              }}
                                                            >
                                                              <span>{o3.label}</span>
                                                              {o3.children.length > 0 && (
                                                                <FIcons.FRight
                                                                  className={styles.itemRightIcon}
                                                                />
                                                              )}
                                                            </div>

                                                            {showAddNewType &&
                                                              o3Index + 1 ===
                                                                o2.children.length && (
                                                                <div
                                                                  className={
                                                                    styles.item +
                                                                    ' ' +
                                                                    styles.itemLatest
                                                                  }
                                                                  onClick={() => {
                                                                    onDropdownClickCustom({
                                                                      value: o2.value,
                                                                      values: o2.values,
                                                                      label: o2.label,
                                                                      labels: o2.labels,
                                                                    });
                                                                  }}
                                                                >
                                                                  {/*<span>添加新类型</span>*/}
                                                                  <span>
                                                                    {FI18n.i18nNext.t(
                                                                      'createresource_selectresourcetype_btn_addnewtype',
                                                                    )}
                                                                  </span>
                                                                </div>
                                                              )}
                                                          </React.Fragment>
                                                        );
                                                      })}
                                                    </div>
                                                  }
                                                >
                                                  <div
                                                    className={styles.item}
                                                    onClick={() => {
                                                      if (o2.children.length === 0) {
                                                        onDropdownChange({
                                                          value: o2.value,
                                                          labels: o2.labels,
                                                        });
                                                      } else {
                                                        ref_input.current?.focus();
                                                      }
                                                    }}
                                                  >
                                                    <span>{o2.label}</span>

                                                    {o2.children.length > 0 && (
                                                      <FIcons.FRight
                                                        className={styles.itemRightIcon}
                                                      />
                                                    )}
                                                  </div>
                                                </Popover>
                                                {showAddNewType &&
                                                  o2Index + 1 === o1.children.length && (
                                                    <div
                                                      className={
                                                        styles.item + ' ' + styles.itemLatest
                                                      }
                                                      onClick={() => {
                                                        onDropdownClickCustom({
                                                          value: o1.value,
                                                          values: o1.values,
                                                          label: o1.label,
                                                          labels: o1.labels,
                                                        });
                                                      }}
                                                    >
                                                      {/*<span>添加新类型</span>*/}
                                                      <span>
                                                        {FI18n.i18nNext.t(
                                                          'createresource_selectresourcetype_btn_addnewtype',
                                                        )}
                                                      </span>
                                                    </div>
                                                  )}
                                              </React.Fragment>
                                            );
                                          })}
                                        </div>
                                      }
                                    >
                                      <div
                                        className={styles.item}
                                        onClick={() => {
                                          if (o1.children.length === 0) {
                                            onDropdownChange({
                                              value: o1.value,
                                              labels: o1.labels,
                                            });
                                          } else {
                                            ref_input.current?.focus();
                                          }
                                        }}
                                      >
                                        <span>{o1.label}</span>
                                        {o1.children.length > 0 && (
                                          <FIcons.FRight className={styles.itemRightIcon} />
                                        )}
                                      </div>
                                    </Popover>
                                    {showAddNewType && o1Index + 1 === o0.children.length && (
                                      <div
                                        className={styles.item + ' ' + styles.itemLatest}
                                        onClick={() => {
                                          onDropdownClickCustom({
                                            value: o0.value,
                                            values: o0.values,
                                            label: o0.label,
                                            labels: o0.labels,
                                          });
                                        }}
                                      >
                                        {/*<span>添加新类型</span>*/}
                                        <span>
                                          {FI18n.i18nNext.t(
                                            'createresource_selectresourcetype_btn_addnewtype',
                                          )}
                                        </span>
                                      </div>
                                    )}
                                  </React.Fragment>
                                );
                              })}
                            </div>
                          }
                        >
                          <div
                            className={styles.item}
                            key={o0.value}
                            onClick={() => {
                              if (o0.children.length === 0) {
                                onDropdownChange({
                                  value: o0.value,
                                  labels: o0.labels,
                                });
                              } else {
                                ref_input.current?.focus();
                              }
                            }}
                          >
                            <div className={styles.itemFirstCatalogue}>
                              <div className={styles.itemFirstCatalogue_name}>{o0.label}</div>
                              {o0.note && (
                                <>
                                  <div
                                    style={{
                                      height: 2,
                                    }}
                                  />
                                  <div className={styles.itemFirstCatalogue_note}>{o0.note}</div>
                                </>
                              )}
                            </div>
                            {o0.children.length > 0 && (
                              <FIcons.FRight className={styles.itemRightIcon} />
                            )}
                          </div>
                        </Popover>
                      );
                    })}
                  </div>
                  <div style={{ height: 10 }} />
                </div>
              </FSimpleBar>
            )}

            {$dropdownOverlayShow_Debounce === 'search' && (
              <FSimpleBar maxHeight={540}>
                <div className={styles.overlay} onClick={(e) => {}}>
                  {$searchResult.length === 0 && (
                    <div
                      style={{
                        height: 128,
                        display: 'flex',
                        alignItems: 'center',
                        flexDirection: 'column',
                        justifyContent: 'center',
                      }}
                    >
                      <FComponentsLib.FContentText
                        // text={'没有找到相关内容'}
                        text={FI18n.i18nNext.t('selectresourcetype_noresaultfound_msg')}
                        type={'normal'}
                        style={{ color: '#666' }}
                      />
                      {subjectType === 'resource' && (
                        <>
                          <div style={{ height: 10 }} />
                          <div
                            style={{
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              gap: 10,
                            }}
                          >
                            <FComponentsLib.FContentText
                              // text={'如需新增自定义类型，可以选择主类型后新增叶子类型'}
                              text={FI18n.i18nNext.t('selectresourcetype_noresaultfound_msg2')}
                              type={'additional2'}
                            />
                            {showAddNewType && (
                              <FComponentsLib.FTextBtn
                                type='primary'
                                onClick={() => {
                                  set$searchInput('');
                                  run_Search();
                                }}
                              >
                                {FI18n.i18nNext.t('selectresourcetype_noresaultfound_btn_back')}
                              </FComponentsLib.FTextBtn>
                            )}
                          </div>
                        </>
                      )}
                    </div>
                  )}
                  {$searchResult.length > 0 &&
                    $searchResult.map((aco, acoi) => {
                      return (
                        <div
                          key={acoi}
                          className={styles.autoCompleteOption2}
                          onClick={() => {
                            onDropdownChange({
                              value: aco.value,
                              labels: aco.labels,
                            });
                          }}
                        >
                          <FTooltip
                            overlayInnerStyle={{
                              width: 'fit-content',
                            }}
                            title={aco.labels.join('/')}
                            mouseEnterDelay={0.5}
                          >
                            <span>{aco.labels.join('/')}</span>
                          </FTooltip>
                          <label>
                            {FI18n.i18nNext.t(
                              'createresource_selectresourcetype_input_resourceqty',
                              {
                                ResourceQty: aco.count,
                              },
                            )}
                          </label>
                        </div>
                      );
                    })}
                </div>
              </FSimpleBar>
            )}
          </>
        );
      }}
    >
      <div
        className={styles.square}
        style={{
          ...(true
            ? {
                backgroundColor: '#f5f5f5',
                cursor: 'not-allowed',
              }
            : {}),
          width,
        }}
        ref={ref}
        onClick={(e) => {
          e.stopPropagation();
          set$dropdownOverlayShow(get$searchInput() === '' ? 'list' : 'search');
          run_Search();
        }}
      >
        <Input
          disabled={disabled}
          ref={ref_input}
          style={{ width, height: 38 }}
          placeholder={
            $searchInputState === 'focus' && !!value
              ? [...value.labels, value.customInput]
                  .filter((v) => {
                    return !!v;
                  })
                  .join('/')
              : // FI18n.i18nNext.t('createresource_selectresourcetype_input_hint')
                placeholder
          }
          value={
            $searchInputState === 'blur' && !!value
              ? [...value.labels, value.customInput]
                  .filter((v) => {
                    return !!v;
                  })
                  .join('/')
              : $searchInput
          }
          onChange={(e) => {
            set$searchInput(e.target.value);
            run_Search();
          }}
          onFocus={() => {
            set$searchInput('');
            set$searchInputState('focus');
          }}
          onBlur={() => {
            set$searchInput('');
            set$searchInputState('blur');
          }}
          suffix={
            <>
              {$searchInput === '' && (
                <>
                  {$dropdownOverlayShow_Debounce !== '' && !disabled ? (
                    <div
                      onClick={(e) => {
                        e.stopPropagation();
                        set$dropdownOverlayShow('');
                      }}
                    >
                      <FIcons.FUp style={{ fontSize: 12 }} />
                    </div>
                  ) : (
                    <div
                      onClick={(e) => {
                        e.stopPropagation();
                        set$dropdownOverlayShow('');
                      }}
                    >
                      <FIcons.FDown style={{ fontSize: 12 }} />
                    </div>
                  )}
                </>
              )}
            </>
          }
        />
      </div>
    </Dropdown>
  );
}

export default FResourceTypeInput3;

function handledData(data: ServerData[], parent: Option | null): Option[] {
  const result: Option[] = data.map((d) => {
    const res: Option = {
      value: d.code,
      label: d.name,
      note: d.note,
      values: [...(parent?.values || []), d.code],
      labels: [...(parent?.labels || []), d.name],
      children: [],
    };

    return {
      ...res,
      children: handledData(d.children, res),
    };
  });
  if (result.length === 0) {
    return [];
  }
  return result;
}
